import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { palette, admin } from '../../styles/tokens';
import { useScheduleYearDensity } from '../../hooks/useScheduleYearDensity';
import { useExpandRecurringForYear } from '../../hooks/useExpandRecurringForYear';

interface YearCanvasContainerProps {
  businessId: string | undefined;
  year: number;
  onSelectMonth: (monthIndex: number) => void;
  onSelectDate: (dateIso: string) => void;
  onToast: (toast: { msg: string; type: 'success' | 'error' }) => void;
}

interface MonthDensity {
  monthIndex: number;
  total: number;
  jobs: number;
  bookings: number;
  shifts: number;
  personal: number;
  subscribed: number;
  activeDays: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildMiniMonthGrid(year: number, monthIndex: number): { iso: string; inMonth: boolean }[] {
  const first = new Date(year, monthIndex, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { iso: isoDate(d), inMonth: d.getMonth() === monthIndex };
  });
}

export function YearCanvasContainer({
  businessId,
  year,
  onSelectMonth,
  onSelectDate,
  onToast,
}: YearCanvasContainerProps) {
  const { rows, loading, error, refresh } = useScheduleYearDensity(businessId, year);
  const expandRecurringForYear = useExpandRecurringForYear();
  const [expanding, setExpanding] = useState(false);
  const autoExpandedRef = useRef<string | null>(null);

  const months = useMemo<MonthDensity[]>(() => {
    const seed: MonthDensity[] = Array.from({ length: 12 }, (_, monthIndex) => ({
      monthIndex,
      total: 0,
      jobs: 0,
      bookings: 0,
      shifts: 0,
      personal: 0,
      subscribed: 0,
      activeDays: 0,
    }));

    for (const row of rows) {
      const date = new Date(`${row.day_date}T12:00:00`);
      if (date.getFullYear() !== year) continue;
      const idx = date.getMonth();
      const bucket = seed[idx];
      const dayTotal =
        row.job_count +
        row.booking_count +
        row.shift_count +
        row.personal_count +
        row.subscribed_count;
      bucket.total += dayTotal;
      bucket.jobs += row.job_count;
      bucket.bookings += row.booking_count;
      bucket.shifts += row.shift_count;
      bucket.personal += row.personal_count;
      bucket.subscribed += row.subscribed_count;
      if (dayTotal > 0) bucket.activeDays += 1;
    }

    return seed;
  }, [rows, year]);

  const maxTotal = useMemo(
    () => Math.max(1, ...months.map((m) => m.total)),
    [months],
  );
  const dayTotals = useMemo(() => {
    const map = new Map<string, { total: number; operatorWork: number; shifts: number; subscribed: number }>();
    for (const row of rows) {
      const operatorWork = row.job_count + row.booking_count + row.personal_count;
      const shifts = row.shift_count;
      const subscribed = row.subscribed_count;
      map.set(row.day_date, {
        total: operatorWork + shifts + subscribed,
        operatorWork,
        shifts,
        subscribed,
      });
    }
    return map;
  }, [rows]);

  const todayIsoValue = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);
  const currentMonthIndex = useMemo(() => {
    const now = new Date();
    if (now.getFullYear() !== year) return -1;
    return now.getMonth();
  }, [year]);

  const handleExpandRecurring = useCallback(async () => {
    if (!businessId) return;
    setExpanding(true);
    const result = await expandRecurringForYear(businessId, year);
    setExpanding(false);

    if (!result.success) {
      onToast({ msg: `Recurring expansion failed: ${result.error.message}`, type: 'error' });
      return;
    }

    onToast({
      msg: `Expanded ${result.data.expanded_count} recurring entries for ${year}`,
      type: 'success',
    });
    await refresh();
  }, [businessId, year, expandRecurringForYear, onToast, refresh]);

  useEffect(() => {
    if (!businessId) return;
    const key = `${businessId}:${year}`;
    if (autoExpandedRef.current === key) return;
    autoExpandedRef.current = key;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time async bootstrap on mount/year change to satisfy spec expand->density flow
    void handleExpandRecurring();
  }, [businessId, year, handleExpandRecurring]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        background: admin.bg,
        minHeight: 500,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800 }}>{year} Schedule Density</div>
          <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>
            Click a month to open calendar zoom.
          </div>
        </div>
        <button
          onClick={() => { void handleExpandRecurring(); }}
          disabled={!businessId || expanding}
          style={{
            border: `1px solid ${palette.g300}`,
            borderRadius: 8,
            background: palette.white,
            color: palette.g700,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 600,
            cursor: !businessId || expanding ? 'not-allowed' : 'pointer',
            opacity: !businessId || expanding ? 0.6 : 1,
            fontFamily: 'inherit',
          }}
        >
          {expanding ? 'Expanding…' : 'Expand recurring'}
        </button>
      </div>

      {error ? (
        <div style={{ color: palette.red, fontSize: 13 }}>{error}</div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
        {months.map((month) => {
          const intensity = month.total / maxTotal;
          const bgAlpha = Math.max(0.05, Math.min(0.22, intensity * 0.2));
          const cells = buildMiniMonthGrid(year, month.monthIndex);
          return (
            <div
              key={month.monthIndex}
              style={{
                textAlign: 'left',
                border: month.monthIndex === currentMonthIndex
                  ? `1px solid ${palette.accent}`
                  : `1px solid ${palette.g200}`,
                borderRadius: 10,
                background: `rgba(59, 130, 246, ${bgAlpha})`,
                padding: 10,
                fontFamily: 'inherit',
              }}
            >
              <button
                onClick={() => onSelectMonth(month.monthIndex)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  color: palette.g800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {MONTHS[month.monthIndex]}
              </button>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 3, marginTop: 8 }}>
                {cells.map((cell) => {
                  const bucket = dayTotals.get(cell.iso) ?? { total: 0, operatorWork: 0, shifts: 0, subscribed: 0 };
                  const dayDots = Math.min(7, bucket.total);
                  const opDots = Math.min(dayDots, bucket.operatorWork);
                  const shiftDots = Math.min(dayDots - opDots, bucket.shifts);
                  const subscribedDots = Math.min(dayDots - opDots - shiftDots, bucket.subscribed);
                  const dots = [
                    ...Array.from({ length: opDots }, () => 'operator' as const),
                    ...Array.from({ length: shiftDots }, () => 'shift' as const),
                    ...Array.from({ length: subscribedDots }, () => 'subscribed' as const),
                  ];
                  while (dots.length < dayDots) dots.push('operator');
                  const isToday = cell.iso === todayIsoValue;
                  return (
                    <button
                      key={cell.iso}
                      onClick={() => onSelectDate(cell.iso)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        minWidth: 0,
                        cursor: cell.inMonth ? 'pointer' : 'default',
                        opacity: cell.inMonth ? 1 : 0.45,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                      disabled={!cell.inMonth}
                    >
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 999,
                          fontSize: 9,
                          color: isToday ? palette.white : palette.g600,
                          background: isToday ? palette.accent : 'transparent',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        {cell.iso.slice(-2).replace(/^0/, '')}
                      </span>
                      <span style={{ display: 'flex', gap: 1, minHeight: 6 }}>
                        {dots.map((dot, idx) => (
                          <span
                            key={`${cell.iso}:${idx}`}
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: dot === 'shift' ? 1 : 4,
                              background: dot === 'operator'
                                ? '#3b82f6'
                                : dot === 'shift'
                                  ? '#14b8a6'
                                  : 'transparent',
                              border: dot === 'subscribed' ? '1px dashed #64748b' : 'none',
                              display: 'inline-block',
                            }}
                          />
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: palette.g600, marginTop: 8 }}>
                {month.total} entries • {month.activeDays} active days
              </div>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: palette.g500 }}>Loading year density…</div>
      ) : null}
    </div>
  );
}
